// /groups/:groupId/fasting/history -- past fasts, with a per-fast share switch.
//
// ONE screen, two views: "Mine" (every fast of the caller's, shared or not) and
// "Group" (members who have shared at least one, each drilling into just those).
// docs/plan-fasting-groups.md, "RESUME HERE: fasting history and sharing".
//
// ⚠ The client never filters anybody's fasts by the share flag. The server only
// sends shared rows for other members, so there is nothing here to forget.
// ⚠ The switch is for the caller's OWN rows only; a member's drill-down has no
// controls at all.

import {
  ApiClientError,
  fetchFastHistory,
  fetchGroup,
  fetchMemberFastHistory,
  setFastShared,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import {
  FAST_END_LABELS,
  GroupRole,
  formatElapsed,
  type FastHistory,
  type FastHistoryEntry,
} from '@msc/shared';

function ms(utc: string): number {
  return Date.parse(`${utc.replace(' ', 'T')}Z`);
}

function dateLabel(utc: string): string {
  return new Date(ms(utc)).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function lengthLabel(f: FastHistoryEntry): string {
  const end = f.endedAt === null ? Date.now() : ms(f.endedAt);
  const hours = Math.max(0, (end - ms(f.startedAt)) / 3_600_000);
  const base = formatElapsed(hours);
  return f.targetHours !== null ? `${base} of ${f.targetHours}h goal` : base;
}

function entryLine(f: FastHistoryEntry): HTMLElement[] {
  const status =
    f.endedAt === null ? 'Running now' : f.endReason ? FAST_END_LABELS[f.endReason] : 'Ended';
  return [
    el('strong', {}, dateLabel(f.startedAt)),
    el('span', { class: 'muted' }, ` · ${lengthLabel(f)} · ${status}`),
  ];
}

export const fastingHistoryView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  const memberParam = ctx.query.get('member');
  const memberId = memberParam && /^\d+$/.test(memberParam) ? Number(memberParam) : null;
  const tab = memberId !== null || ctx.query.get('view') === 'group' ? 'group' : 'mine';
  const base = `/groups/${groupId}/fasting/history`;

  let canManage = false;
  let showsBoard = true;
  try {
    const group = await fetchGroup(groupId);
    canManage = group.myRole >= GroupRole.ADMIN;
    showsBoard = groupShowsLeaderboard(group.allowedEventTypes);
  } catch {
    // Nav furniture only; the history read below reports a real access problem.
  }

  const fail = (err: unknown): void => {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    const denied = err instanceof ApiClientError && (err.status === 403 || err.status === 404);
    main.replaceChildren(
      el('p', { class: 'form-error' }, denied ? 'Not available.' : 'Could not load the history.'),
    );
  };

  const frame = (body: HTMLElement[]): void => {
    main.replaceChildren(
      groupSubnav(groupId, 'fasting', canManage, true, showsBoard),
      el('div', { class: 'page-head' }, [
        el('h1', {}, 'Fasting history'),
        el('p', { class: 'small' }, [el('a', { href: `/groups/${groupId}/fasting` }, 'Back to fasting')]),
      ]),
      el('nav', { class: 'subnav' }, [
        el('a', tab === 'mine' ? { href: base, class: 'active', 'aria-current': 'page' } : { href: base }, 'Mine'),
        el(
          'a',
          tab === 'group'
            ? { href: `${base}?view=group`, class: 'active', 'aria-current': 'page' }
            : { href: `${base}?view=group` },
          'Group',
        ),
      ]),
      ...body,
    );
  };

  if (memberId !== null) {
    try {
      const m = await fetchMemberFastHistory(groupId, memberId);
      frame([
        el('p', { class: 'small' }, [el('a', { href: `${base}?view=group` }, 'All members')]),
        el('h2', {}, m.displayName),
        m.fasts.length === 0
          ? el('p', { class: 'muted' }, 'No shared fasts.')
          : el('ul', { class: 'plain-list' }, m.fasts.map((f) => el('li', {}, entryLine(f)))),
      ]);
    } catch (err) {
      fail(err);
    }
    return;
  }

  let history: FastHistory;
  try {
    history = await fetchFastHistory(groupId);
  } catch (err) {
    fail(err);
    return;
  }

  function render(h: FastHistory): void {
    if (tab === 'group') {
      frame([
        h.members.length === 0
          ? el('p', { class: 'muted' }, 'Nobody has shared a fast yet.')
          : el(
              'ul',
              { class: 'plain-list' },
              h.members.map((m) =>
                el('li', {}, [
                  el('a', { href: `${base}?member=${m.userId}` }, m.displayName),
                  el(
                    'span',
                    { class: 'muted' },
                    ` · ${m.count} shared · longest ${formatElapsed(m.longestHours)}`,
                  ),
                ]),
              ),
            ),
      ]);
      return;
    }

    const status = el('p', { class: 'muted small', role: 'status' });
    const rows = h.mine.map((f) => {
      const box = el('input', {
        type: 'checkbox',
        'aria-label': `Share the fast starting ${dateLabel(f.startedAt)} with the group`,
      }) as HTMLInputElement;
      box.checked = f.sharedWithGroup;
      box.addEventListener('change', async () => {
        box.setAttribute('disabled', 'disabled');
        status.textContent = 'Saving';
        try {
          history = await setFastShared(groupId, f.sessionId, box.checked);
          render(history);
        } catch {
          box.checked = !box.checked;
          status.textContent = 'Could not save. Try again.';
          box.removeAttribute('disabled');
        }
      });
      return el('li', {}, [
        el('div', {}, entryLine(f)),
        el('label', { class: 'check' }, [box, ' Share with group']),
      ]);
    });

    frame([
      el(
        'p',
        { class: 'muted small' },
        'Your fasts are private unless you share them. Sharing is per fast and you can change it any time.',
      ),
      h.mine.length === 0 ? el('p', { class: 'muted' }, 'No fasts yet.') : el('ul', { class: 'plain-list' }, rows),
      status,
    ]);
  }

  render(history);
};
